import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Loader } from 'lucide-react';

const normalizeYouTubeEmbedUrl = (rawUrl) => {
  if (!rawUrl) {
    return '';
  }

  try {
    const parsed = new URL(rawUrl.trim());
    const host = parsed.hostname.toLowerCase();
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = parsed.pathname.replace(/^\//, '');
    } else if (host.endsWith('youtube.com')) {
      if (parsed.pathname === '/watch') {
        videoId = parsed.searchParams.get('v') || '';
      } else if (parsed.pathname.startsWith('/embed/')) {
        videoId = parsed.pathname.split('/embed/')[1] || '';
      } else if (parsed.pathname.startsWith('/live/')) {
        videoId = parsed.pathname.split('/live/')[1] || '';
      }
    }

    const cleanVideoId = videoId.split(/[?&/]/)[0].trim();
    if (!cleanVideoId) {
      return '';
    }

    return `https://www.youtube-nocookie.com/embed/${cleanVideoId}?autoplay=1&rel=0&modestbranding=1`;
  } catch (error) {
    return '';
  }
};

const PrivateStreamEmbed = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [streamEmbedUrl, setStreamEmbedUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (location.state?.streamEmbedUrl) {
      setStreamEmbedUrl(normalizeYouTubeEmbedUrl(location.state.streamEmbedUrl));
      return;
    }

    setError('Stream session not found. Please verify your access from the meetings page again.');
  }, [location.state]);

  const goBack = () => {
    if (user?.role === 'ADMIN') {
      navigate('/admin/meetings', { replace: true });
      return;
    }

    if (user?.role === 'SPEAKER') {
      navigate('/speaker/meetings', { replace: true });
      return;
    }

    navigate('/user/meetings', { replace: true });
  };

  if (error) {
    return (
      <div style={styles.wrapper}>
        <div style={{ ...styles.card, backgroundColor: theme.colors.cardBg, borderColor: theme.colors.border }}>
          <AlertCircle size={40} color={theme.colors.danger} />
          <h2 style={{ ...styles.title, color: theme.colors.text }}>Unable to Open Stream</h2>
          <p style={{ ...styles.message, color: theme.colors.textSecondary }}>{error}</p>
          <button style={styles.primaryButton} onClick={goBack}>Back to Meetings</button>
        </div>
      </div>
    );
  }

  if (!streamEmbedUrl) {
    return (
      <div style={styles.wrapper}>
        <div style={{ ...styles.card, backgroundColor: theme.colors.cardBg, borderColor: theme.colors.border }}>
          <Loader size={34} style={styles.spinner} color={theme.colors.primary} />
          <p style={{ ...styles.message, color: theme.colors.textSecondary }}>Preparing private stream...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerBar}>
        <button onClick={goBack} style={styles.leaveButton}>Leave Stream</button>
      </div>
      <iframe
        src={streamEmbedUrl}
        title="Private Meeting Stream"
        style={styles.iframe}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
      />
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    height: '100vh',
    position: 'fixed',
    inset: 0,
    backgroundColor: '#000000',
  },
  headerBar: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 3,
  },
  leaveButton: {
    padding: '10px 14px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#1d4ed8',
    color: '#ffffff',
    fontWeight: '600',
    cursor: 'pointer',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(140deg, #e9f0ff 0%, #f8fafc 100%)',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '520px',
    borderRadius: '14px',
    border: '1px solid',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)',
  },
  title: {
    margin: '14px 0 8px',
    fontSize: '22px',
    fontWeight: '700',
  },
  message: {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.6',
  },
  primaryButton: {
    marginTop: '20px',
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontWeight: '600',
    cursor: 'pointer',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
  },
};

export default PrivateStreamEmbed;
